import { SearchOutlined } from "@ant-design/icons";
import { Input, Space, Table, Typography } from "antd";
import { debounce } from "lodash";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { ApiNames, deleteSavedListAPI, getNumberOfPatientsAPI } from "../../api/api";
import { ReactComponent as DeleteIcon } from "../../assets/svg/delete-new-icon.svg";
import { ReactComponent as NoFavoriteListFoundIcon } from "../../assets/svg/no-favorite-list-found.svg";
import { ReactComponent as NoSavedListFoundIcon } from "../../assets/svg/no-saved-list-found.svg";
import { ModalComponent } from "../../component/customComponent/ModalComponent";
import { GRAY_500 } from "../../constant/colors";
import { defaultSavedListFilters } from "../../constant/filters";
import { SAVED_LIST_PAGE_SIZE } from "../../constant/general";
import { Routes } from "../../constant/routes";
import { filtersTypes, filtersTypesOptions } from "../../constant/savedList";
import { ASCEND, DECEND, SORT_DIRECTIONS_ASC_DESC_ASC, apiSortOptions } from "../../constant/sort";
import { fontWeights, sizes } from "../../constant/styles";
import ACTION from "../../store/action";
import {
  selectIsFetching,
  selectSavedListData,
  selectSavedListTableColumns,
  selectedFilterListQuery,
  selectedFilterListType,
  selectedFilterSorting,
  selectedSavedListActiveColumn
} from "../../store/selector";
import { scrollTableToTop } from "../../utils/misc";
import { dateSort, stringSort } from "../../utils/sort";
import { ReactComponent as NoSharedListFoundIcon } from "../../assets/svg/no-shared-list-found.svg";
import { TableStyle } from "./TableStyle";
import { SavedListActionsMenu } from "../SavedList/SavedListActionsMenu";
import { ListNameColumn, NumberOfRecordsColumn, StarFilterListColumn } from "../customComponent/CommonColumns";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import SuccessMessage from "../customComponent/customMessages/SuccessMessage";
import { RadioGroup } from "../customComponent/Group";
import { FilterListForm } from "./FilterListForm";
import SavedListPageCount from "./SavedListPageCount";
import LinkableCell from "../LinkableCell";

const { Text } = Typography;
const tableId = "saved-list-table";

const StyledNoListSpace = styled(Space)`
  width: 470px;
  padding: 40px;
`;
const StyledNoListTitle = styled(Text)`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.large};
`;
const StyledNoListDescription = styled(Text)`
  color: ${GRAY_500};
  font-size: ${sizes.medium};
  white-space: pre-line;
  white-space: pre-line;
`;

const StyledTable = styled(Table)`
  ${TableStyle}
  &.saved-list-table .ant-table-row {
    cursor: pointer;
  }
  .ant-table {
    max-height: calc(100vh - 320px);
  }
  .ant-table-empty {
    max-height: calc(100vh - 260px);
    .ant-table-tbody {
      height: calc(100vh - 350px);
    }
  }
`;

const renderWithLink = (record, renderFn) => {
  if (record.isValid) {
    return <LinkableCell to={`${Routes.SAVEDLISTS}/${record.id}/list`}>{renderFn()}</LinkableCell>;
  }

  return renderFn();
};

const SavedListTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const tableColumns = useSelector(selectSavedListTableColumns);
  const isFetching = useSelector(selectIsFetching([ApiNames.searchSavedList]));
  const tableData = useSelector(selectSavedListData);
  const selectedListType = useSelector(selectedFilterListType);
  const searchQuery = useSelector(selectedFilterListQuery);
  const activeTabSorting = useSelector(selectedFilterSorting);
  const activeColumn = useSelector(selectedSavedListActiveColumn);

  const [currentPage, setCurrentPage] = useState(tableData?.selectedPage || 1);
  const [savedListsRecordCounts, setSavedListsRecordCounts] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const searchSavedListAct = (page) => dispatch(ACTION.searchSavedListAction(page));
  const setSavedListSortByAct = (sortBy) => dispatch(ACTION.updateSavedListSortBy(sortBy));
  const setSavedListOrderByAct = (orderBy) => dispatch(ACTION.updateSavedListOrderBy(orderBy));
  const setSelectedFilterTypeAct = (listType) => dispatch(ACTION.setSelectedFilterListType(listType));
  const setListNameQueryAct = (query) => dispatch(ACTION.setListNameQueryAction(query));
  const setFormPopupsAct = (visible) => dispatch(ACTION.setFormPopups(visible));

  const location = useLocation();

  const sortTooltips = {
    [ASCEND]: t("columns.click_to_sort_descending"),
    [DECEND]: t("columns.click_to_cancel_sorting"),
    null: t("columns.click_to_sort_ascending")
  };

  useEffect(() => {
    searchSavedListAct(currentPage);
  }, [selectedListType, searchQuery, location]);

  const fetchDataCount = async (savedList) => {
    try {
      const params = typeof savedList?.params === "string" ? JSON.parse(savedList.params) : savedList?.params;

      const { data } = await getNumberOfPatientsAPI(
        params?.filters,
        defaultSavedListFilters.skip,
        defaultSavedListFilters.limit,
        savedList.selectedView
      );

      setSavedListsRecordCounts((prevState) => ({
        ...prevState,
        [savedList.id]: {
          success: true,
          total: data?.total
        }
      }));
    } catch (error) {
      setSavedListsRecordCounts((prevState) => ({
        ...prevState,
        [savedList.id]: {
          total: null,
          success: false
        }
      }));
    }
  };

  useEffect(() => {
    if (tableData?.data?.data) {
      tableData.data.data.forEach(fetchDataCount);
    }
    setCurrentPage(tableData.selectedPage);
  }, [tableData]);

  const getSortOrder = (columnName) => {
    if (!activeColumn || activeColumn.name !== columnName || !activeTabSorting) return null;
    return activeTabSorting[columnName] === apiSortOptions.asc ? ASCEND : DECEND;
  };

  const getNextSortTooltip = (columnName) => sortTooltips[getSortOrder(columnName)];

  const columns = [
    {
      dataIndex: tableColumns.star,
      width: 50,
      key: tableColumns.star?.key,
      align: "left",
      render: (text, record) => <StarFilterListColumn page={currentPage} record={record} />
    },
    {
      ...tableColumns?.name,
      render: (text, record) => renderWithLink(record, () => <ListNameColumn record={record} />),
      sorter: {
        compare: (a, b, direction) => stringSort(a.name, b.name, direction)
      },
      sortDirections: SORT_DIRECTIONS_ASC_DESC_ASC,
      sortOrder: getSortOrder("name"),
      showSorterTooltip: {
        title: getNextSortTooltip("name"),
        align: { targetOffset: ["46%", "0"] }
      }
    },
    {
      ...tableColumns?.updatedAt,
      render: (text, record) => renderWithLink(record, () => moment(record.updatedAt).format("MMM D, YYYY")),
      sorter: {
        compare: (a, b, direction) => dateSort(a.updatedAt, b.updatedAt, direction)
      },
      sortDirections: SORT_DIRECTIONS_ASC_DESC_ASC,
      width: 150,
      sortOrder: getSortOrder("updatedAt"),
      showSorterTooltip: {
        placement: "top",
        title: getNextSortTooltip("updatedAt"),
        align: { targetOffset: ["12%", "0"] }
      }
    },
    {
      dataIndex: tableColumns?.numberOfRecords,
      width: 150,
      key: tableColumns?.numberOfRecords?.key,
      title: tableColumns?.numberOfRecords?.title,
      render: (text, record) =>
        renderWithLink(record, () => (
          <NumberOfRecordsColumn record={record} savedListsRecordCounts={savedListsRecordCounts} />
        ))
    },
    {
      ...tableColumns?.actions?.tableColumns,
      align: "center",
      width: 71,
      render: (record) => (
        <SavedListActionsMenu
          record={record}
          recordCount={savedListsRecordCounts[record.id]?.total}
          source="saved_lists"
          deleteAction={() => {
            setSelectedRow(record);
            setShowDeleteModal(true);
          }}
        />
      )
    }
  ];

  const onFilterChange = (e) => {
    setCurrentPage(1);
    setSelectedFilterTypeAct(e.target.value);
  };

  const onTableChange = (pagination, filters, sorter) => {
    const { current: nextPage } = pagination;
    if (nextPage === currentPage) {
      const sortedColumns = Array.isArray(sorter) ? sorter : [sorter];

      if (!sortedColumns.length || !sortedColumns[0].order) {
        setCurrentPage(1);
        setSavedListOrderByAct(null);
        setSavedListSortByAct(null);
        searchSavedListAct(1);
        return;
      }

      const activeSortedColumn = sortedColumns[0];
      let { order: nextSortBy, column: { key: nextColumnName } = {} } = activeSortedColumn;

      const columnIschanged = nextColumnName !== (activeColumn?.name || "");

      nextSortBy = nextSortBy === DECEND ? apiSortOptions.desc : apiSortOptions.asc;
      const prevSortBy = activeTabSorting?.[nextColumnName];
      const sortDirectionIschanged = prevSortBy !== nextSortBy;

      if (columnIschanged || sortDirectionIschanged) {
        setCurrentPage(1);
        setSavedListOrderByAct(nextColumnName);
        setSavedListSortByAct(nextSortBy);
        searchSavedListAct(1);
      }
    }
    scrollTableToTop(tableId);
  };

  const onSearchHandler = (e) => {
    setCurrentPage(1);
    setListNameQueryAct(e.target.value);
  };

  const debouncedSearchChangeHandler = useCallback(debounce(onSearchHandler, 500), []);

  return (
    <div>
      <Space
        style={{
          justifyContent: "space-between",
          display: "flex",
          marginTop: "15px",
          marginRight: "50px",
          marginLeft: "50px"
        }}
      >
        <div id="list_search">
          <Input
            style={{ width: "400px", borderRadius: "4px" }}
            placeholder={t("savedList.search_placeholder")}
            defaultValue={searchQuery}
            allowClear
            prefix={<SearchOutlined />}
            onChange={debouncedSearchChangeHandler}
          />
        </div>
        <RadioGroup
          onChange={onFilterChange}
          options={filtersTypesOptions}
          defaultValue={selectedListType || filtersTypes.all}
          buttonStyle="solid"
          t={t}
          id="saved_lists_all_options_lists"
        />
      </Space>

      <StyledTable
        className="saved-list-table"
        id={tableId}
        size="small"
        rowClassName={({ isValid }) => (!isValid ? "disabled-row" : "cursor-pointer")}
        tableLayout="fixed"
        loading={isFetching}
        columns={columns}
        onChange={onTableChange}
        locale={{
          emptyText:
            tableData &&
            tableData?.data?.data?.length == 0 &&
            (selectedListType === filtersTypes.starred ? (
              <StyledNoListSpace direction={"vertical"} size={10} align={"center"}>
                <NoFavoriteListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_favorite_to_show" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_favorite_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ) : selectedListType === filtersTypes.shared ? (
              <StyledNoListSpace direction={"vertical"} size={10} align={"center"}>
                <NoSharedListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_shared_lists_to_found" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_shared_lists_to_found_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ) : (
              <StyledNoListSpace direction={"vertical"} size={10} align={"center"}>
                <NoSavedListFoundIcon />
                <StyledNoListTitle>
                  {t(searchQuery === "" ? "no_saved_lists_to_show" : "no_saved_lists_found")}
                </StyledNoListTitle>
                {searchQuery === "" && (
                  <StyledNoListDescription>{t("no_saved_lists_to_show_description")}</StyledNoListDescription>
                )}
              </StyledNoListSpace>
            ))
        }}
        pagination={{
          size: "small",
          showSizeChanger: false,
          current: currentPage,
          onChange: (page) => {
            setCurrentPage(page);
            searchSavedListAct(page);
            window.scrollTo(0, 0);
          },
          defaultPageSize: SAVED_LIST_PAGE_SIZE,
          pageSize: SAVED_LIST_PAGE_SIZE,
          position: ["bottom"],
          total: tableData?.data?.total,
          showTotal: (total, range) => (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <SavedListPageCount total={total} range={range} isAccurateCount={true} />
            </div>
          )
        }}
        dataSource={
          tableData && tableData?.data?.data?.length > 0
            ? tableData.data.data
                .map((list, index) => {
                  return {
                    ...list,
                    key: index
                  };
                })
                .sort((a, b) => b.isActive - a.isActive)
            : null
        }
      />
      <FilterListForm isEditView={true} setFormPopupsAct={setFormPopupsAct} />
      <ModalComponent
        open={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        okText={t("ok_text")}
        onOk={async () => {
          try {
            const deleteListRes = await deleteSavedListAPI(selectedRow?.id);
            if (deleteListRes) {
              let nextExpectedPage = currentPage;
              const isLastRecord = tableData.total - 1 === 0;

              if ((tableData.total - 1) % SAVED_LIST_PAGE_SIZE === 0 && !isLastRecord && currentPage > 1) {
                nextExpectedPage = currentPage - 1;
              }

              setCurrentPage(nextExpectedPage);
              searchSavedListAct(nextExpectedPage);
              setShowDeleteModal(false);

              SuccessMessage(t("savedList.confirm_modal.success_message"));
            }
          } catch (error) {
            ErrorMessage(t("savedList.confirm_modal.error_message"));
          }
        }}
      >
        <Space direction="vertical">
          <DeleteIcon style={{ width: "48px" }} />
          <Text className="title">{t("savedList.confirm_modal.title")}</Text>
          <Text className="content">
            {t("savedList.confirm_modal.content", {
              listName: selectedRow?.name
            })}
          </Text>
        </Space>
      </ModalComponent>
    </div>
  );
};

export { SavedListTable };
